import {
    Headline,
    Loading,
    Text,
    useMerchantConfig,
    useThemedStyle,
} from "@venuepos/react-common";
import { formatAmount } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useMemo } from "react";
import { View } from "react-native";
import { CompareData } from "../merchant-dashboard";
import { dashboardStyleFunc } from "../styles";
import { useComparisonWidths } from "./use-comparison-widths";

export const InvoiceAverageTotal = React.memo(
    function InvoiceAverageTotalComponent({
        data,
        compareTo,
        locale,
    }: {
        data?: CompareData;
        compareTo?: CompareData;
        locale?: AvailableLocale;
    }) {
        const merchantConfig = useMerchantConfig();
        const [t] = useTranslation();
        const sharedStyles = useThemedStyle(dashboardStyleFunc);

        const { baseDataWidth, compareDataWidth, noData } = useComparisonWidths(
            data?.dashboard?.totals.avgTransaction ?? 0,
            compareTo?.dashboard?.totals.avgTransaction ?? 0,
            data?.date,
            compareTo?.date
        );

        const formattedBaseAmount = useMemo(
            () =>
                formatAmount(
                    data?.dashboard?.totals.avgTransaction,
                    merchantConfig.currency,
                    {
                        locale,
                        printCurrency: true,
                    }
                ),
            [
                data?.dashboard?.totals.avgTransaction,
                merchantConfig.currency,
                locale,
            ]
        );

        const formattedCompareAmount = useMemo(
            () =>
                formatAmount(
                    compareTo?.dashboard?.totals.avgTransaction,
                    merchantConfig.currency,
                    {
                        locale,
                        printCurrency: true,
                    }
                ),
            [
                compareTo?.dashboard?.totals.avgTransaction,
                merchantConfig.currency,
                locale,
            ]
        );

        return (
            <>
                <View style={sharedStyles.widgetContentHolder}>
                    <Headline style={sharedStyles.totalHeadline} size="h5">
                        {`${t(
                            "dashboard.widgets.avg_transaction",
                            "Avg. transaction"
                        )} *`}
                    </Headline>
                    <View>
                        {!data ? (
                            <Loading message={t("common.loading", "Loading")} />
                        ) : (
                            <View style={sharedStyles.dataRow}>
                                {!compareTo ? (
                                    <View style={sharedStyles.dataSingleValue}>
                                        <Text style={sharedStyles.totalValue}>
                                            {formattedBaseAmount}
                                        </Text>
                                    </View>
                                ) : (
                                    <>
                                        <Text style={[sharedStyles.totalValue]}>
                                            {data.date}
                                        </Text>
                                        <Text style={sharedStyles.totalValue}>
                                            {formattedBaseAmount}
                                        </Text>
                                    </>
                                )}
                            </View>
                        )}

                        {!compareTo ? null : (
                            <>
                                <View
                                    style={[
                                        sharedStyles.graphHolder,
                                        noData
                                            ? sharedStyles.graphHolderEmpty
                                            : null,
                                    ]}
                                >
                                    {!noData ? (
                                        <>
                                            <View
                                                style={[
                                                    sharedStyles.defaultBar,
                                                    baseDataWidth >=
                                                    compareDataWidth
                                                        ? sharedStyles.positiveBar
                                                        : sharedStyles.negativeBar,
                                                    {
                                                        width: `${baseDataWidth}%`,
                                                    },
                                                ]}
                                            />
                                            <View
                                                style={[
                                                    sharedStyles.defaultBar,
                                                    {
                                                        width: `${compareDataWidth}%`,
                                                    },
                                                ]}
                                            />
                                        </>
                                    ) : (
                                        <Text style={sharedStyles.noDataText}>
                                            {t(
                                                "dashboard.no_data_plural",
                                                "No data for the selected dates"
                                            )}
                                        </Text>
                                    )}
                                </View>
                                <View style={sharedStyles.dataRow}>
                                    <Text style={sharedStyles.compareValue}>
                                        {compareTo?.date}
                                    </Text>
                                    <Text style={[sharedStyles.compareValue]}>
                                        {formattedCompareAmount}
                                    </Text>
                                </View>
                            </>
                        )}
                    </View>
                </View>
                <Text style={sharedStyles.noteText}>
                    {`* ${t(
                        "dashboard.turnover_disclaimer",
                        "Turnover is excluding giftcard usage"
                    )}`}
                </Text>
            </>
        );
    }
);
